import { useRef, useState } from "react";
import { Volume2, VolumeX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * <ReadAloudButton /> — Wave 26.3.
 *
 * Inline TTS button. Tap to play `text` aloud via /api/hidro/voice/speak.
 * Tap again to stop. Identical UX to the speaker icon in Hidro full-
 * screen mode, just decoupled and reusable.
 *
 * Used by:
 *   - Job title (view + edit modes)
 *   - JC brief / description
 *   - Each task title
 *   - Each task comment
 *   - Customer rating notes (portal)
 *   - …anywhere a tech might need long-form text read back hands-free.
 *
 * Props:
 *   text          — string to read aloud (silently no-ops if empty)
 *   voice         — optional override (default: backend chooses)
 *   size          — 'xs' | 'sm' | 'md' (default 'sm')
 *   className     — passthrough
 *   testId        — data-testid hook
 */

// Module-level cache so re-tapping the same comment doesn't refetch.
const _cache = new Map(); // key -> blob url
const _CACHE_LIMIT = 30;

let _currentAudio = null; // global so a second tap on a different button stops the first

export default function ReadAloudButton({
  text,
  voice = "alloy",
  size = "sm",
  className = "",
  testId = "read-aloud-btn",
}) {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const sizeCls =
    size === "xs"
      ? "w-5 h-5 text-[10px]"
      : size === "md"
      ? "w-7 h-7 text-sm"
      : "w-6 h-6 text-xs";
  const iconCls = size === "xs" ? "w-3 h-3" : size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";

  const stop = () => {
    try {
      audioRef.current?.pause();
    } catch (_e) {
      /* noop */
    }
    audioRef.current = null;
    setPlaying(false);
  };

  const play = async () => {
    const clean = (text || "").trim();
    if (!clean) return;
    // Tapping while playing → stop
    if (playing) {
      stop();
      return;
    }
    // Stop any other ReadAloudButton currently playing
    if (_currentAudio) {
      try {
        _currentAudio.pause();
      } catch (_e) {
        /* noop */
      }
      _currentAudio = null;
    }

    const key = `${voice}::${clean}`;
    let blobUrl = _cache.get(key);
    setBusy(true);
    try {
      if (!blobUrl) {
        const resp = await api.post(
          "/hidro/voice/speak",
          { text: clean, voice },
          { responseType: "blob" },
        );
        blobUrl = URL.createObjectURL(resp.data);
        if (_cache.size >= _CACHE_LIMIT) {
          const firstKey = _cache.keys().next().value;
          const oldUrl = _cache.get(firstKey);
          _cache.delete(firstKey);
          try {
            URL.revokeObjectURL(oldUrl);
          } catch (_e) {
            /* noop */
          }
        }
        _cache.set(key, blobUrl);
      }
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      _currentAudio = audio;
      setPlaying(true);
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          setPlaying(false);
        }
        if (_currentAudio === audio) _currentAudio = null;
      };
      audio.onerror = () => {
        setPlaying(false);
        toast.error("Couldn't play audio.");
      };
      await audio.play();
    } catch (e) {
      setPlaying(false);
      const msg = e?.response?.data?.detail || e?.message || "Couldn't read that aloud.";
      toast.error(typeof msg === "string" ? msg : "Couldn't read that aloud.");
    } finally {
      setBusy(false);
    }
  };

  if (!(text || "").trim()) return null;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        play();
      }}
      disabled={busy && !playing}
      className={`inline-flex items-center justify-center rounded-full transition shrink-0 ${
        playing
          ? "bg-[#1FB6C1]/20 text-[#1FB6C1] border border-[#1FB6C1]/40"
          : "bg-white/[0.04] hover:bg-[#1FB6C1]/10 text-[#9aa3b5] hover:text-[#1FB6C1] border border-white/10 hover:border-[#1FB6C1]/40"
      } ${sizeCls} ${className}`}
      title={playing ? "Stop" : "Read aloud"}
      aria-label={playing ? "Stop reading" : "Read aloud"}
      data-testid={testId}
    >
      {busy && !playing ? (
        <Loader2 className={`${iconCls} animate-spin`} />
      ) : playing ? (
        <VolumeX className={iconCls} />
      ) : (
        <Volume2 className={iconCls} />
      )}
    </button>
  );
}
